/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useContext, useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import {
  createResponseContext,
  createResponseNonPaginationContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from "../../../../_mcp/helpers";
import { Merchant, MerchantDetail, MerchantQuery } from "./_model";
import { useQueryRequest } from "./QueryRequestProvider";
import { getMerchants, getMerchant } from "./_request";

// Contexts
const QueryResponseContext =
  createResponseContext<Merchant>(initialQueryResponse);
const MerchantDetailContext =
  createResponseNonPaginationContext<MerchantDetail>(initialQueryResponse);

// Provider untuk List
const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { state } = useQueryRequest();
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state));
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state]);

  const convertParamToObject = () => {
    const paramUrl: string = query;
    const paramToObject: any = JSON.parse(
      '{"' +
        decodeURI(paramUrl)
          .replace(/"/g, '\\"')
          .replace(/&/g, '","')
          .replace(/=/g, '":"') +
        '"}'
    );

    return paramToObject;
  };

  const removeBlankAttributes = (query: MerchantQuery) => {
    return Object.fromEntries(
      Object.entries(query).filter(([_, v]) => v != null)
    );
  };

  const getObjectFilter = (query: any) => {
    const objectFilter: any = {};
    Object.keys(query).forEach(function (key) {
      if (key.includes("filter_") && key !== "filter_batch") {
        const newKey = key.replace(/^filter_/, "");
        objectFilter[newKey] = query[key];
      }
    });
    return objectFilter;
  };

  const queryPayload = () => {
    const paramObj: any = convertParamToObject();
    const filterObj = getObjectFilter(paramObj);
    const page = paramObj.page;
    const limit = paramObj.limit;
    const sort = !!paramObj.sort
      ? paramObj.order === "asc"
        ? `${paramObj.sort}`
        : `-${paramObj.sort}`
      : "-createdAt";
    const search = !!paramObj.search ? paramObj.search : null;
    const sanitizedQuery: MerchantQuery = {
      paging: {
        page: page,
        limit: limit,
        sort: sort,
      },
      merchantName: search,
      ...filterObj,
    };
    const newQuery = removeBlankAttributes(sanitizedQuery);

    return newQuery;
  };

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery);
    }
  }, [updatedQuery]);

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.CHECK_USER_FUNDS_LIST}-${query}`,
    () => {
      const newQuery = queryPayload();
      return getMerchants(newQuery);
    },
    {
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, refetch, response, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  );
};

// Provider untuk Detail Merchant
const MerchantDetailProvider: FC<WithChildren & { id: string | number }> = ({
  children,
  id,
}) => {
  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    [`${QUERIES.CHECK_USER_FUNDS_LIST}-detail`, id],
    () => getMerchant({ id }),
    {
      enabled: !!id,
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );

  return (
    <MerchantDetailContext.Provider
      value={{ isLoading: isFetching, refetch, response, query: String(id) }}
    >
      {children}
    </MerchantDetailContext.Provider>
  );
};

// Hooks untuk list
const useQueryResponse = () => useContext(QueryResponseContext);

const useQueryResponseData = () => {
  const { response } = useQueryResponse();
  if (!response) {
    return [];
  }
  return response.detailData.docs;
};

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    ...initialQueryState,
  };
  const { response } = useQueryResponse();
  if (!response || !response.detailData) {
    return defaultPaginationState;
  }
  const newResponse = { ...response.detailData };
  delete newResponse.docs;
  return newResponse;
};

const useQueryResponseLoading = (): boolean => {
  const { isLoading } = useQueryResponse();
  return isLoading;
};

// Hooks untuk detail
const useMerchantDetail = () => useContext(MerchantDetailContext);
const useMerchantDetailData = () => {
  const { response } = useMerchantDetail();
  return response?.detailData || null;
};
const useMerchantDetailLoading = (): boolean => {
  const { isLoading } = useMerchantDetail();
  return isLoading;
};

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
  MerchantDetailProvider,
  useMerchantDetail,
  useMerchantDetailData,
  useMerchantDetailLoading,
};
